import { useRef, useEffect } from "react";
import { Box, Divider, Container, Stack } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import numberToWords from "number-to-words";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

const ThermalPrint = () => {
  const location = useLocation();
  const { data, tableData, qrData } = location.state;
  const componentRef = useRef();
  const company = useSelector((state) => state.common.companyInfo);
  const navigate = useNavigate();
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);
  const subCurrency = useSelector((state) => state.common.subCurrency);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // This will be called after the printing is completed
      navigate(-2); // Navigate back to the previous page
    },
    pageStyle: `
      @page {
        size: 80mm auto; /* Standard thermal printer width, auto height */
        margin: 0;
      }
      body {
        margin: 0;
        -webkit-print-color-adjust: exact; /* Crucial for background colors and precise rendering */
      }
      /* Ensure text is readable and correctly aligned */
      p, small, h2, table {
        font-family: monospace; /* Often best for thermal printers */
        font-size: 12px; /* Adjust as needed for readability */
        line-height: 1.2;
        margin: 0; /* Remove default margins */
        padding: 0; /* Remove default paddings */
      }
      .thermal-print-container {
        width: 80mm; /* Fixed width for the print area */
        padding: 5mm; /* Small padding around the content */
        box-sizing: border-box; /* Include padding in the width */
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      th, td {
        padding: 2px 0; /* Adjust cell padding */
      }
      .text-left { text-align: left; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .flex-between {
        display: flex;
        justify-content: space-between;
        width: 100%; /* Ensure flex container takes full width */
      }
      .company-header h2, .company-header p {
        text-align: center;
      }
      .divider {
        border-top: dashed 1px #8c8b8b;
        margin: 5px 0;
      }
      .qr-code-section {
        display: flex;
        justify-content: center;
        margin-top: 10px;
      }
    `,
  });

  function convertNumberToWordsWithCurrency(number) {
    const paisaAmount = (number * 100) % 100;
    const words = numberToWords.toWords(Math.floor(number));
    const paisaInWords = numberToWords.toWords(paisaAmount);
    return `${words} ${primaryCurrency} and ${paisaInWords} ${subCurrency}`;
  }

  const amount = data.BillNetTotalField.toFixed(decimalCount);
  const result = convertNumberToWordsWithCurrency(amount);

  useEffect(() => {
    handlePrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]); // Dependency on company ensures print re-runs if company info changes

  return (
    <div>
      <Box ref={componentRef} className="thermal-print-container">
        {/* Header section */}
        <div className="company-header">
          <h2 style={{ margin: "0", fontSize: "16px" }}>{company.Name}</h2>
          <p style={{ margin: "0", fontSize: "12px" }}>{company.Add1}</p>
          <p style={{ margin: "0", fontSize: "12px" }}>{company.Ph}</p>
        </div>

        <div className="divider"></div>

        <div>
          <div className="flex-between">
            <p style={{ margin: "1px", fontSize: "12px" }}>
              {data.BillNo
                ? `Inv. No: ${data.BillNo}`
                : `Ret. No: ${data.RetNo}`}
            </p>
            <p style={{ margin: "1px", fontSize: "12px" }}>
              DATE: <span>{dayjs(data.BillDate).format("DD/MM/YYYY")}</span>
            </p>
          </div>
          <p style={{ margin: "1px", fontSize: "12px" }}>
            Customer Name:{" "}
            <span>
              {data.BillPartyName === "3" ? "Cash" : data.BillPartyName}
            </span>
          </p>
          <div className="flex-between">
            <p style={{ margin: "1px", fontSize: "12px" }}>
              State: <span>KERALA</span>
            </p>
            <p style={{ margin: "1px", fontSize: "12px" }}>
              Veh: <span>0</span>
            </p>
          </div>

          <table
            style={{ width: "100%", marginTop: "5px", marginBottom: "5px" }}
          >
            <thead>
              <tr>
                <th
                  className="text-left"
                  style={{
                    borderBlock: "dashed 1px #8c8b8b",
                    fontWeight: "normal",
                  }}
                >
                  SI
                </th>
                <th
                  className="text-left"
                  style={{
                    borderBlock: "dashed 1px #8c8b8b",
                    fontWeight: "normal",
                  }}
                >
                  Item
                </th>
                <th
                  className="text-right"
                  style={{
                    borderBlock: "dashed 1px #8c8b8b",
                    fontWeight: "normal",
                  }}
                >
                  Qty
                </th>
                <th
                  className="text-right"
                  style={{
                    borderBlock: "dashed 1px #8c8b8b",
                    fontWeight: "normal",
                  }}
                >
                  Price
                </th>
                <th
                  className="text-right"
                  style={{
                    borderBlock: "dashed 1px #8c8b8b",
                    fontWeight: "normal",
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((item, index) => (
                <>
                  <tr key={index}>
                    <td className="text-left">{index + 1}</td>
                    <td className="text-left" colSpan={4}>
                      {item.ItemNameTextField}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right"></td>
                    <td className="text-right"></td>
                    <td className="text-right">{item.qty}</td>
                    <td className="text-right">{item.Rate1}</td>
                    <td className="text-right">
                      {item.total.toFixed(decimalCount)}
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
          </table>

          <div className="divider"></div>

          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td className="text-right" style={{ width: "100%" }}>
                  Grand Total :
                </td>
                <td className="text-right">
                  {data.BillTotalField.toFixed(decimalCount)}
                </td>
              </tr>
              <tr>
                <td className="text-right" style={{ width: "100%" }}>
                  Discount Amt :
                </td>
                <td className="text-right">{data.BillDiscAmtField}</td>
              </tr>
              <tr>
                <td className="text-right" style={{ width: "100%" }}>
                  VAT % :
                </td>
                <td className="text-right">{data.BillTaxField}</td>
              </tr>
              <tr>
                <td className="text-right" style={{ width: "100%" }}>
                  Total :
                </td>
                <td className="text-right">
                  {data.BillNetTotalField.toFixed(decimalCount)}
                </td>
              </tr>
            </tbody>
          </table>

          <div
            className="flex-between"
            style={{ paddingBottom: "0.4rem"}}
          >
            <small className="text-center">
              Total Qty: <span>{tableData.length}</span>
            </small>
          </div>

          <div style={{ textAlign: "left" }}>
            <small >
              Amount In Words : <span>{result}</span>
            </small>
          </div>

          <div className="divider"></div>
          <div className="divider"></div>
        </div>

        <Stack
          direction="row"
          justifyContent="center"
          className="qr-code-section"
        >
          <Box width={100}>
            <QrCodeGenerator qrData={data.zatca_einv_qr_code || qrData} />
          </Box>
        </Stack>
      </Box>
    </div>
  );
};

export default ThermalPrint;
